import { ErrorBoundary } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { Box } from 'native-base';
import React, { useMemo } from 'react';
import { FlatList, StyleProp, ViewStyle } from 'react-native';

import { ReportGeneratorListItemNormalized } from '../../../../../../store/reportGenerator/types';
import { reportGeneratorTableHead } from '../../../../constants/table';
import { reportGeneratorTheme } from '../../../../constants/theme';
import { ReportGeneratorItem } from '../ReportGeneratorItem';
import { DocumentsTableSkeletonRow } from '../SkeletonRow';

interface DocumentsTableListProps {
  items: ReportGeneratorListItemNormalized[];
  isLoadingMore?: boolean;
  scrollEnabled: boolean;
  onEndReached?: () => void;
  listStyle?: StyleProp<ViewStyle>;
  onDownload: (item: ReportGeneratorListItemNormalized, onComplete: () => void) => void;
  onRetry: (item: ReportGeneratorListItemNormalized, onComplete: () => void) => void;
  itemSeparatorSpace?: string;
  listBackground?: boolean;
  listContentPadding?: boolean;
  listContentFlexGrow?: number;
  appliedFiltersCount?: number;
}

const DocumentsTableEmptyState: React.FC<{ appliedFiltersCount: number }> = ({
  appliedFiltersCount,
}) => {
  const hasActiveFilters = appliedFiltersCount > 0;

  return (
    <Box py="stack.xxs" px="inline.xxxs" bg="transparent" width="full">
      <ErrorBoundary
        errorIconOrIllustration="NotFoundIllustration"
        errorTitle={
          hasActiveFilters
            ? 'Nenhum resultado corresponde à sua pesquisa'
            : 'Nenhum relatório solicitado ainda'
        }
        errorMessage={
          hasActiveFilters
            ? 'Tente ajustar os filtros aplicados e pesquisar novamente.'
            : 'Quando você solicitar um relatório, ele aparecerá aqui no histórico.'
        }
      />
    </Box>
  );
};

export const DocumentsTableList: React.FC<DocumentsTableListProps> = ({
  items,
  isLoadingMore,
  scrollEnabled,
  onEndReached,
  listStyle,
  onDownload,
  onRetry,
  itemSeparatorSpace = reportGeneratorTheme.table.itemGap,
  listBackground = true,
  listContentPadding = true,
  listContentFlexGrow,
  appliedFiltersCount = 0,
}) => {
  const listSurfaceColor = listBackground ? reportGeneratorTheme.table.rowBg : 'transparent';
  const contentPadding = listContentPadding ? Number.parseInt(Spacing['inline.xxxs'], 10) : 0;
  const contentFlexGrow = listContentFlexGrow ?? (listBackground ? undefined : 0);
  const columnFlex = useMemo(() => reportGeneratorTableHead.map((column) => column.flex || 1), []);

  const renderRow = (item: ReportGeneratorListItemNormalized) => (
    <ReportGeneratorItem
      item={item}
      columnFlex={columnFlex}
      onDownload={onDownload}
      onRetry={onRetry}
    />
  );

  const renderListItem = (item: ReportGeneratorListItemNormalized) =>
    listBackground ? renderRow(item) : <Box bg="transparent">{renderRow(item)}</Box>;

  const listFooter = isLoadingMore ? (
    <Box py="stack.xxxs" bg={listSurfaceColor}>
      <DocumentsTableSkeletonRow />
    </Box>
  ) : (
    <Box h="stack.xxxs" />
  );

  return (
    <FlatList
      data={items}
      maxToRenderPerBatch={20}
      updateCellsBatchingPeriod={5}
      showsVerticalScrollIndicator={false}
      scrollEnabled={scrollEnabled}
      nestedScrollEnabled={scrollEnabled}
      onEndReachedThreshold={0.4}
      keyExtractor={(item) => item.id}
      onEndReached={scrollEnabled ? onEndReached : undefined}
      style={
        scrollEnabled
          ? [
              { backgroundColor: listSurfaceColor },
              ...(Array.isArray(listStyle) ? listStyle : listStyle ? [listStyle] : []),
            ]
          : { backgroundColor: listSurfaceColor }
      }
      ItemSeparatorComponent={() => <Box h={itemSeparatorSpace} bg="transparent" />}
      contentContainerStyle={{
        backgroundColor: listSurfaceColor,
        paddingVertical: contentPadding,
        paddingHorizontal: contentPadding,
        ...(contentFlexGrow !== undefined ? { flexGrow: contentFlexGrow } : {}),
      }}
      ListEmptyComponent={<DocumentsTableEmptyState appliedFiltersCount={appliedFiltersCount} />}
      ListFooterComponent={listFooter}
      renderItem={({ item }) => renderListItem(item)}
    />
  );
};
